/**
 * Places Types Service
 * Service for managing place types (restaurant, hotel, enterprise, etc.)
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';
const API_KEY = 'cabaza_api_key_2024_change_me';

const headers = {
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY
};

/**
 * Get all place types
 * @param {Object} options - Filter options
 * @param {string} options.category - Filter by category
 * @param {string} options.keyword - Search keyword
 * @param {string} options.lang - Language (fr, en, nl, de)
 * @param {boolean} options.active - Filter by active status
 */
export const getPlacesTypes = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.category) params.append('category', options.category);
  if (options.keyword) params.append('keyword', options.keyword);
  if (options.lang) params.append('lang', options.lang);
  if (options.active !== undefined) params.append('active', options.active);

  const url = `${API_BASE_URL}/places_types${params.toString() ? '?' + params.toString() : ''}`;
  const response = await fetch(url, { headers });
  return response.json();
};

/**
 * Get place types grouped by category
 * @param {string} lang - Language (fr, en, nl, de)
 */
export const getPlacesTypesGrouped = async (lang = 'fr') => {
  const response = await fetch(`${API_BASE_URL}/places_types/grouped?lang=${lang}`, { headers });
  return response.json();
};

/**
 * Get all categories
 */
export const getCategories = async () => {
  const response = await fetch(`${API_BASE_URL}/places_types/categories`, { headers });
  return response.json();
};

/**
 * Get a single place type by ID
 * @param {number} id - Type ID
 * @param {string} lang - Language
 */
export const getPlaceTypeById = async (id, lang = 'fr') => {
  const response = await fetch(`${API_BASE_URL}/places_types/${id}?lang=${lang}`, { headers });
  return response.json();
};

/**
 * Get a single place type by code
 * @param {string} code - Type code
 * @param {string} lang - Language
 */
export const getPlaceTypeByCode = async (code, lang = 'fr') => {
  const response = await fetch(`${API_BASE_URL}/places_types/code/${code}?lang=${lang}`, { headers });
  return response.json();
};

/**
 * Create a new place type
 * @param {Object} typeData - Type data
 */
export const createPlaceType = async (typeData) => {
  const response = await fetch(`${API_BASE_URL}/places_types`, {
    method: 'POST',
    headers,
    body: JSON.stringify(typeData)
  });
  return response.json();
};

/**
 * Update a place type
 * @param {number} id - Type ID
 * @param {Object} typeData - Type data to update
 */
export const updatePlaceType = async (id, typeData) => {
  const response = await fetch(`${API_BASE_URL}/places_types/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(typeData)
  });
  return response.json();
};

/**
 * Delete a place type
 * @param {number} id - Type ID
 */
export const deletePlaceType = async (id) => {
  const response = await fetch(`${API_BASE_URL}/places_types/${id}`, {
    method: 'DELETE',
    headers
  });
  return response.json();
};

/**
 * Category labels for display
 */
export const categoryLabels = {
  food: { fr: 'Alimentation', en: 'Food & Dining', icon: 'UtensilsCrossed' },
  shopping: { fr: 'Commerce', en: 'Shopping', icon: 'ShoppingBag' },
  services: { fr: 'Services', en: 'Services', icon: 'Briefcase' },
  health: { fr: 'Santé & Beauté', en: 'Health & Beauty', icon: 'Heart' },
  sports: { fr: 'Sports & Loisirs', en: 'Sports & Leisure', icon: 'Trophy' },
  entertainment: { fr: 'Culture & Divertissement', en: 'Entertainment', icon: 'Film' },
  hospitality: { fr: 'Hébergement', en: 'Hospitality', icon: 'Hotel' },
  events: { fr: 'Événements', en: 'Events', icon: 'Calendar' },
  social: { fr: 'Social & Communauté', en: 'Social & Community', icon: 'Users' },
  education: { fr: 'Éducation', en: 'Education', icon: 'GraduationCap' },
  transport: { fr: 'Transport', en: 'Transport', icon: 'Car' },
  business: { fr: 'Entreprise & Industrie', en: 'Business & Industry', icon: 'Building2' },
  government: { fr: 'Gouvernement & Public', en: 'Government & Public', icon: 'Landmark' }
};

export default {
  getPlacesTypes,
  getPlacesTypesGrouped,
  getCategories,
  getPlaceTypeById,
  getPlaceTypeByCode,
  createPlaceType,
  updatePlaceType,
  deletePlaceType,
  categoryLabels
};
